<template>
  <div class="dynamic-buttons-dialog">
    <v-form ref="form" v-model="isFormValid">
      <v-row class="dynamic-buttons-dialog-rows">
        <v-col cols="12">
          <v-text-field
            type="text"
            persistent-placeholder
            placeholder="https://example.com"
            v-model="formData.globalLink"
            label="Global Link (for all buttons)"
            :rules="urlRules"></v-text-field>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="6" class="mb-4">
          <v-text-field
            type="number"
            v-model.number="formData.mobileColumns"
            label="Mobile Columns"
            hint="Number of columns on mobile"
            min="1"
            :rules="[v => !!v || 'Mobile columns is required']"
            required></v-text-field>
        </v-col>
        <v-col cols="6">
          <v-text-field
            type="number"
            v-model.number="formData.desktopColumns"
            label="Desktop Columns"
            hint="Number of columns on desktop"
            min="1"
            :rules="[v => !!v || 'Desktop columns is required']"
            required></v-text-field>
        </v-col>
      </v-row>

      <div v-for="(button, index) in formData.buttons" :key="index" class="button-container">
        <div class="d-flex align-center mb-4">
          <v-text-field
            type="text"
            v-model="button.text"
            label="Button Text"
            class="flex-grow-1 mr-2"
            :rules="[v => !!v || 'Button text is required']"
            required></v-text-field>
          <v-btn icon color="error" @click="removeButton(index)" class="mt-1">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </div>

        <div class="ml-4">
          <v-text-field
            type="text"
            v-model="button.link"
            persistent-placeholder
            placeholder="Leave empty to use global link"
            label="Override Link (optional)"
            :rules="urlRules"></v-text-field>
          <v-select
            :menu-props="{ offsetY: true, offsetOverflow: true }"
            v-model="button.target"
            label="Target"
            :items="targetOptions"></v-select>
          <VaColorPicker
            :position="'top'"
            :title="'Background Color'"
            v-model="button.backgroundColor"
            class="mb-2"></VaColorPicker>
          <VaColorPicker
            :position="'top'"
            :title="'Text Color'"
            v-model="button.textColor"
            class="mb-2"></VaColorPicker>
          <VaColorPicker
            :position="'top'"
            :title="'Bottom Border Color'"
            v-model="button.shadowColor"
            class="mb-2"></VaColorPicker>
        </div>
      </div>

      <v-btn color="primary" class="mt-4" block @click="addButton">
        <v-icon left>mdi-plus</v-icon>
        Add Button
      </v-btn>

      <v-alert v-if="errorMessage" type="error" class="mt-4" dismissible @input="errorMessage = ''">
        {{ errorMessage }}
      </v-alert>
    </v-form>
  </div>
</template>

<script>
  import { urlValidation } from "@/utility/urlValidation";
  import { DYNAMIC_BUTTONS_EVENT_TYPES } from "@/constant";
  import VaColorPicker from "@/components/custom/VaColorPicker.vue";

  export default {
    components: {
      VaColorPicker,
    },
    data() {
      return {
        isFormValid: false,
        formData: {
          globalLink: "",
          mobileColumns: 1,
          desktopColumns: 2,
          buttons: [
            {
              text: "No",
              link: "",
              target: "_self",
              backgroundColor: "#FF4145",
              textColor: "#ffffff",
              shadowColor: "#BE5B5B",
            },
            {
              text: "Yes",
              link: "",
              target: "_self",
              backgroundColor: "#1EC95B",
              textColor: "#ffffff",
              shadowColor: "#147F3A",
            },
          ],
        },
        errorMessage: "",
        urlRules: [v => !v || !!urlValidation(v) || this.$t("generic.validation.url")],
        targetOptions: [
          { value: "_self", text: "Same window tab" },
          { value: "_blank", text: "New window tab" },
        ],
      };
    },
    watch: {
      isFormValid: {
        handler() {
          this.sendError();
        },
      },
      formData: {
        handler() {
          this.validateAndUpdate();
        },
        deep: true,
      },
    },
    beforeMount() {
      this.setupReceiveDataListener();
      this.requestData();
    },
    methods: {
      addButton() {
        this.formData.buttons.push({
          text: "",
          link: "",
          target: "_self",
          backgroundColor: "#28b02b",
          textColor: "#ffffff",
          shadowColor: "#1a7a1c",
        });
      },
      removeButton(index) {
        if (this.formData.buttons.length > 1) {
          this.formData.buttons.splice(index, 1);
        } else {
          this.errorMessage = "At least one button is required";
        }
      },
      async validateAndUpdate() {
        if (this.$refs.form) {
          await this.$refs.form.validate();
          if (this.isFormValid) {
            this.updateValue();
            this.errorMessage = "";
          }
        }
      },
      sendError() {
        window.parent.postMessage(
          {
            mceAction: DYNAMIC_BUTTONS_EVENT_TYPES.VALIDATION,
            data: {
              isValid: this.isFormValid,
            },
          },
          origin,
        );
      },
      updateValue() {
        window.parent.postMessage(
          {
            mceAction: DYNAMIC_BUTTONS_EVENT_TYPES.UPDATE_DYNAMIC_BUTTONS_DATA,
            data: this.formData,
          },
          origin,
        );
      },

      setupReceiveDataListener() {
        window.onmessage = async message => {
          if (message.data.type === DYNAMIC_BUTTONS_EVENT_TYPES.NEED_VALIDATION) {
            await this.$refs.form.validate();
          }
          if (message.data.type === DYNAMIC_BUTTONS_EVENT_TYPES.SEND_DATA) {
            const { data } = message.data;
            if (data === "undefined") {
              this.updateValue();
            } else {
              this.formData = {
                globalLink: data.globalLink || "",
                mobileColumns: data.mobileColumns || 1,
                desktopColumns: data.desktopColumns || 2,
                buttons: data.buttons || [
                  {
                    text: "No",
                    link: "",
                    target: "_self",
                    backgroundColor: "#FF4145",
                    textColor: "#ffffff",
                    shadowColor: "#BE5B5B",
                  },
                  {
                    text: "Yes",
                    link: "",
                    target: "_self",
                    backgroundColor: "#1EC95B",
                    textColor: "#ffffff",
                    shadowColor: "#147F3A",
                  },
                ],
              };
            }
          }
        };
      },
      requestData() {
        window.parent.postMessage(
          {
            mceAction: DYNAMIC_BUTTONS_EVENT_TYPES.REQUEST_DATA,
          },
          origin,
        );
      },
    },
  };
</script>

<style lang="scss" scoped>
  .dynamic-buttons-dialog {
    margin-bottom: 30px;
    padding: 30px 12px 0px;
  }

  .dynamic-buttons-dialog-rows {
    margin-bottom: 15px;
  }

  .button-container {
    margin-bottom: 24px;
    padding: 16px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;
  }
</style>
